import type { Grid, Token, TokenColor } from "../types.ts";
import {
  BACKGROUND,
  CELL_BG,
  GRID_LINE,
  WALL_COLOR,
  HOLE_COLOR,
  HOLE_RING,
  TOKEN_FILLS,
  TOKEN_STROKES,
  GOAL_FILLS,
  GOAL_BORDERS,
  TELEPORTER_COLORS,
} from "./colors.ts";

export interface GridLayout {
  cellSize: number;
  offsetX: number;
  offsetY: number;
}

export function computeGridLayout(
  grid: Grid,
  width: number,
  height: number
): GridLayout {
  const margin = 24;
  const maxCellW = (width - margin * 2) / grid.cols;
  const maxCellH = (height - margin * 2) / grid.rows;
  const cellSize = Math.min(Math.floor(Math.min(maxCellW, maxCellH)), 80);
  const offsetX = (width - cellSize * grid.cols) / 2;
  const offsetY = (height - cellSize * grid.rows) / 2;
  return { cellSize, offsetX, offsetY };
}

export function drawGrid(
  ctx: CanvasRenderingContext2D,
  grid: Grid,
  tokens: Token[],
  width: number,
  height: number
): void {
  const layout = computeGridLayout(grid, width, height);
  const { cellSize, offsetX, offsetY } = layout;

  // Background
  ctx.fillStyle = BACKGROUND;
  ctx.fillRect(0, 0, width, height);

  // Draw cells
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      const cell = grid.cells[r]![c]!;
      const x = offsetX + c * cellSize;
      const y = offsetY + r * cellSize;
      const pad = 1;

      // Cell background
      ctx.fillStyle = CELL_BG;
      ctx.fillRect(x + pad, y + pad, cellSize - pad * 2, cellSize - pad * 2);

      // Goal square
      if (cell.goalColor) {
        drawGoal(ctx, x, y, cellSize, cell.goalColor);
      }

      // Hole
      if (cell.isHole) {
        drawHole(ctx, x, y, cellSize);
      }

      // Teleporter
      if (cell.teleporterId != null) {
        drawTeleporter(ctx, x, y, cellSize, cell.teleporterId);
      }
    }
  }

  // Draw grid lines
  ctx.strokeStyle = GRID_LINE;
  ctx.lineWidth = 0.5;
  for (let r = 0; r <= grid.rows; r++) {
    ctx.beginPath();
    ctx.moveTo(offsetX, offsetY + r * cellSize);
    ctx.lineTo(offsetX + grid.cols * cellSize, offsetY + r * cellSize);
    ctx.stroke();
  }
  for (let c = 0; c <= grid.cols; c++) {
    ctx.beginPath();
    ctx.moveTo(offsetX + c * cellSize, offsetY);
    ctx.lineTo(offsetX + c * cellSize, offsetY + grid.rows * cellSize);
    ctx.stroke();
  }

  // Draw walls (thick lines over grid lines)
  ctx.strokeStyle = WALL_COLOR;
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      const cell = grid.cells[r]![c]!;
      const x = offsetX + c * cellSize;
      const y = offsetY + r * cellSize;

      // Only draw south and east walls to avoid duplicates
      // (north/west walls are drawn by the neighboring cell's south/east)
      // Exception: top row draws north, left column draws west
      if (r === 0 && cell.walls.north) {
        drawWallLine(ctx, x, y, x + cellSize, y);
      }
      if (c === 0 && cell.walls.west) {
        drawWallLine(ctx, x, y, x, y + cellSize);
      }
      if (cell.walls.south) {
        drawWallLine(ctx, x, y + cellSize, x + cellSize, y + cellSize);
      }
      if (cell.walls.east) {
        drawWallLine(ctx, x + cellSize, y, x + cellSize, y + cellSize);
      }
    }
  }

  // Draw tokens
  for (const token of tokens) {
    if (token.lost) continue;
    const cx = offsetX + token.pos.col * cellSize + cellSize / 2;
    const cy = offsetY + token.pos.row * cellSize + cellSize / 2;
    const radius = cellSize * 0.35;

    // Shadow
    ctx.beginPath();
    ctx.arc(cx + 2, cy + 2, radius, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.fill();

    // Token
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fillStyle = TOKEN_FILLS[token.color];
    ctx.fill();
    ctx.strokeStyle = TOKEN_STROKES[token.color];
    ctx.lineWidth = 2;
    ctx.stroke();

    // Highlight
    ctx.beginPath();
    ctx.arc(cx - radius * 0.2, cy - radius * 0.2, radius * 0.4, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.2)";
    ctx.fill();
  }
}

function drawGoal(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: TokenColor
): void {
  const pad = size * 0.12;
  const cx = x + size / 2;
  const cy = y + size / 2;
  const r = size / 2 - pad;

  // Filled background
  ctx.fillStyle = GOAL_FILLS[color];
  ctx.fillRect(x + pad, y + pad, size - pad * 2, size - pad * 2);

  // Diamond shape
  ctx.beginPath();
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx + r, cy);
  ctx.lineTo(cx, cy + r);
  ctx.lineTo(cx - r, cy);
  ctx.closePath();
  ctx.strokeStyle = GOAL_BORDERS[color];
  ctx.lineWidth = 2;
  ctx.setLineDash([4, 3]);
  ctx.stroke();
  ctx.setLineDash([]);
}

function drawHole(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number
): void {
  const cx = x + size / 2;
  const cy = y + size / 2;
  const r = size * 0.3;

  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = HOLE_COLOR;
  ctx.fill();
  ctx.strokeStyle = HOLE_RING;
  ctx.lineWidth = 2;
  ctx.stroke();
}

function drawTeleporter(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  pairId: number
): void {
  const cx = x + size / 2;
  const cy = y + size / 2;
  const r = size * 0.28;
  const color = TELEPORTER_COLORS[pairId % TELEPORTER_COLORS.length]!;

  // Outer ring
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  ctx.stroke();

  // Inner ring
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.5, 0, Math.PI * 2);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

function drawWallLine(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): void {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}
